import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './variantButton.scss';
import classNames from 'classnames';
import TabNavigationHelper from '../../../../../helpers/tabNavigationHelper';
import { VariantType } from '../../../../../types';
import { VariantStore } from '../../../../../stores/VariantStore';
import Diameter from '../../../../icons/diameter';
import Length from '../../../../icons/length';
import Cross from '../../../../icons/cross';
import ArrowSmallIcon from '../../../../icons/arrowSmallIcon';
import { IBrickItem } from '../../../../../stores/types';
import TextHelper from '../../../../../helpers/textHelper';

export interface IVariantButton {
    variantStore?: VariantStore;
    variantType: VariantType;
    isMobileHeader?: boolean;
    isDeactive?: boolean;
    isStickyHeader?: boolean;
    brickItem: IBrickItem
}

@inject('variantStore')
@observer
export default class VariantButton extends React.Component<IVariantButton> {
    private tabNavigation: TabNavigationHelper;
    private width: { width: number };

    constructor(props: IVariantButton) {
        super(props);
        this.tabNavigation = new TabNavigationHelper();

        this.width = { width: 1024 };

        this.onClick = this.onClick.bind(this);
    }

    private get colorButton() {
        const imgSrc =
            this.props.variantStore?.mainStore.cdnAssetsUrl +
            'colors64px/ColorIcon40/' +
            this.props.brickItem.selectedColor?.code.toString() +
            '.png';
        const colorName = TextHelper.addWhitespacesAroundSlashes(
            this.props.brickItem.selectedColor?.name
        );
        return (
            <>
                <img
                    role='color-icn'
                    className={styles.colorIcon}
                    src={imgSrc}
                    title={this.props.brickItem.selectedColor?.name}
                />
                <div role='btn-title' className={classNames(styles.button_label, 'notranslate',
                    { [styles.mobile_header_label]: this.props.isMobileHeader })}>
                    { colorName }
                </div >
            </>
        );
    }

    private get sizeButton() {
        if (!this.props.brickItem) {
            return <></>
        }
        return (
            <>
                <div role='value' className={styles.selected_value}>
                    {this.props.brickItem.selectedSize?.name}
                </div>
            </>
        );
    }

    private get specialButton() {
        const sizeTypeContent = () => {
            return (
                <div className={styles.special}>
                    <Diameter />
                    <div className={styles.text}>
                        <div className={styles.value}>{
                            this.props.brickItem.selectedSizeType}</div>
                    </div>
                </div>
            );
        };
        const valueContent = () => {
            return (
                <div className={styles.special}>
                    <Length />
                    <div className={styles.text}>
                        <div className={styles.value}>{
                            this.props.brickItem.selectedSize?.name
                        }</div>
                    </div>
                </div>
            );
        };

        const valueClassNames = [styles.selected_value, styles.special_value].join(' ');
        return (
            <>
                <div role='value' className={valueClassNames}>
                    {sizeTypeContent()}
                    {valueContent()}
                </div>
            </>
        );
    }

    private get modelButton() {
        const valueClassNames = [styles.selected_value, styles.model_value].join(' ');
        return (
            <div role='value' className={valueClassNames}>
                {this.props.brickItem.selectedModel?.label}
            </div>
        );
    }

    private onClick(): void {
        if (this.props.variantStore?.isVariantSelectionPanelOpen
            && this.props.variantStore?.variantType === this.props.variantType)
            this.props.variantStore.changeVariantSelectionPanelDisplay(false, undefined, undefined, true);
        else this.props.variantStore?.changeVariantSelectionPanelDisplay(true, this.props.variantType,
            this.width.width);
    };

    private label(labelText: string, captionText = '', insideButton = false) {
        if (this.props.isStickyHeader) {
            return null;
        }
        const labelClassNames = classNames([styles.variant_title], {
            [styles.variantLabelInsideButton]: insideButton,
        });

        return (
            <div className={styles.variant_label_container}>
                <div role='var-title' className={labelClassNames}>
                    {labelText}
                </div>
                {captionText && <div className={styles.variant_caption}>{captionText}</div>}
            </div>
        );
    };

    private buttonContent() {
        switch (this.props.variantType) {
            case VariantType.Color: {
                return <>{this.colorButton}</>;
            }
            case VariantType.Size: {
                return <>{this.sizeButton}</>;
            }
            case VariantType.Special: {
                return <>{this.specialButton}</>;
            }
            case VariantType.Model: {
                return <>{this.modelButton}</>;
            }
            default:
        }
    };

    private buttonIcon() {
        const showCross = this.props.variantStore?.variantType === this.props.variantType
            && this.props.variantStore?.isVariantSelectionPanelOpen;
        if (this.props.isDeactive) {
            return <></>;
        }

        const selectText = this.props.variantStore?.mainStore.localization.generalL10n.select;

        if (showCross) {
                return <Cross />;
            } else if (this.props.isMobileHeader) {
                return <ArrowSmallIcon />
            } else {
                return (
                    <div className={styles.select_bubble}>
                        <span className={styles.select_bubble_text}>{ selectText }</span>
                        <ArrowSmallIcon />
                    </div>
                );
        }
    };

    private content() {
        const generalL10n = this.props.variantStore?.mainStore.localization.generalL10n;

        type VariantsWithContext = VariantType.Color | VariantType.Size | VariantType.Special | VariantType.Model;

        const calculateCaption = (variantType: VariantsWithContext): string => {
            if (this.props.isMobileHeader) {
                return ''
            }
            const numberOfVariants = (() => {
                switch (variantType) {
                    case VariantType.Color:
                        return this.props.brickItem.variantSelection.availableColors.length;
                    case VariantType.Model:
                        return this.props.brickItem.variantSelection.availableModels.length;
                    case VariantType.Size:
                    case VariantType.Special:
                        return this.props.brickItem.variantSelection.availableSizeTypes.reduce(
                            (sum, sizeType) => sum + sizeType.sizes.length, 0
                        );
                    default:
                        return 0;
                }
            })();

            if (
                !numberOfVariants ||
                numberOfVariants < 1 ||
                (variantType === VariantType.Color && numberOfVariants === 1)
            ) {
                return '';
            }

            const captionText = numberOfVariants === 1 ? generalL10n?.variant : generalL10n?.variants;
            return `${numberOfVariants} ${captionText}`;
        }

        switch (this.props.variantType) {
            case VariantType.Color: {
                const testId = 'variant-color-button';
                const labelText = `${generalL10n?.textColor}`;
                const caption = calculateCaption(VariantType.Color);
                return (
                    <>
                        {this.label(labelText, caption)}
                        {this.button(testId)}
                    </>
                );
            }
            case VariantType.Size: {
                const testId = 'variant-size-button';
                const labelText = `${generalL10n?.textSize}`;
                const caption = calculateCaption(VariantType.Size);
                return (
                    <>
                        {this.label(labelText, caption)}
                        {this.button(testId)}
                    </>
                );
            }
            case VariantType.Special: {
                const specialVariantTitle = `${generalL10n?.textSize}`;
                const testId = 'variant-special-button';
                const caption = calculateCaption(VariantType.Size);
                return (
                    <>
                        {this.label(specialVariantTitle, caption)}
                        {this.button(testId)}
                    </>
                );
            }
            case VariantType.Model: {
                const testId = 'variant-model-button';
                const labelText = `${generalL10n?.textModel}`;
                const caption = calculateCaption(VariantType.Model);
                return (
                    <>
                        {this.label(labelText, caption)}
                        {this.button(testId)}
                    </>
                );
            }
            default:
        }
    };

    private button(testId: string) {
        // select another variant type button, if current one is deactivated.
        return (
            <div
                role='button'
                data-testid={testId}
                className={classNames(styles.button, {
                    [styles.variant_special_button]: this.props.variantType === VariantType.Special,
                    [styles.selected]: this.props.variantStore?.variantType === this.props.variantType
                        && this.props.variantStore?.isVariantSelectionPanelOpen,
                })}
                onClick={this.onClick}
                ref={(el) => this.tabNavigation.setFocusAtt(el)}
            >
                {this.buttonContent()}
                {this.buttonIcon()}
            </div>
        );
    };

    get variantButtonCount(): number {
        let variantButtonCount = 0;

        if (!this.props.brickItem.variantSelection) {
            return variantButtonCount;
        }

        if (this.props.brickItem.variantSelection.showColorSwitcher) {
            variantButtonCount++;
        }
        if (this.props.brickItem.variantSelection.showSizeSwitcher) {
            variantButtonCount++;
        }
        if (this.props.brickItem.variantSelection.showModelSwitcher) {
            variantButtonCount++;
        }
        return variantButtonCount;
    }

    private get isOnlyVariantButton() {
        return this.variantButtonCount === 1 ?
            [styles.only_variant_button] : '';
    };

    private get isColorVariantButton() {
        return this.props.variantType === VariantType.Color ? [styles.color_variant_button] : '';
    }

    render() {
        return (
            <div
                data-testid='variant-button'
                className={classNames(styles.variant_button, this.isOnlyVariantButton,
                    this.isColorVariantButton,
                    { [styles.deactive]: this.props.isDeactive },
                    {
                        [styles.selected]: !this.props.isDeactive &&
                            this.props.variantStore?.variantType === this.props.variantType
                            && this.props.variantStore?.isVariantSelectionPanelOpen
                    },
                    { [styles.isStickyHeader]: this.props.isStickyHeader })
                }>
                {this.content()}
            </div>
        );
    }
}

